export const SPATIUS_SDK_AUDIO_SELECTOR = 'audio[id^="audio-"]';

export type SpatiusSdkAudioCleanupResult = {
  removedCount: number;
  elementIds: string[];
};

export type SpatiusSdkAudioSilenceResult = SpatiusSdkAudioCleanupResult & {
  mutedCount: number;
  mutedElementIds: string[];
  preservedCount: number;
  preservedElementIds: string[];
};

export type SpatiusSdkAudioCleanupObserver = (result: SpatiusSdkAudioSilenceResult) => void;

export function getSpatiusSdkAudioElements(root?: ParentNode): HTMLAudioElement[] {
  if (!root && typeof document === "undefined") {
    return [];
  }

  const scope = root ?? document;
  return Array.from(scope.querySelectorAll<HTMLAudioElement>(SPATIUS_SDK_AUDIO_SELECTOR)).filter(
    (element) =>
      element.autoplay &&
      !element.controls &&
      element.style.display === "none" &&
      element.parentElement === document.body
  );
}

export function cleanupSpatiusSdkAudioElements(root?: ParentNode): SpatiusSdkAudioCleanupResult {
  const elements = getSpatiusSdkAudioElements(root);
  const elementIds: string[] = [];

  for (const element of elements) {
    elementIds.push(element.id);

    try {
      element.pause();
    } catch {
      // Best-effort cleanup for SDK-owned hidden media elements.
    }

    try {
      element.muted = true;
      (element as HTMLAudioElement & { srcObject?: MediaStream | null }).srcObject = null;
      element.removeAttribute("src");
      element.load();
    } catch {
      // Best-effort cleanup for SDK-owned hidden media elements.
    }

    element.remove();
  }

  return {
    removedCount: elements.length,
    elementIds,
  };
}

export function silenceSpatiusSdkAudioElementsForLipSync(
  root?: ParentNode
): SpatiusSdkAudioSilenceResult {
  const elements = getSpatiusSdkAudioElements(root);
  const elementIds: string[] = [];
  const mutedElementIds: string[] = [];
  const preservedElementIds: string[] = [];

  for (const element of elements) {
    elementIds.push(element.id);

    try {
      element.pause();
    } catch {
      // Best-effort cleanup for SDK-owned hidden media elements.
    }

    try {
      element.muted = true;
      (element as HTMLAudioElement & { srcObject?: MediaStream | null }).srcObject = null;
      element.removeAttribute("src");
      element.load();
    } catch {
      // Best-effort cleanup for SDK-owned hidden media elements.
    }

    element.remove();
  }

  return {
    removedCount: elementIds.length,
    elementIds,
    mutedCount: mutedElementIds.length,
    mutedElementIds,
    preservedCount: preservedElementIds.length,
    preservedElementIds,
  };
}

function nodeContainsSpatiusSdkAudioElement(node: Node): boolean {
  if (!(node instanceof Element)) {
    return false;
  }

  if (node.matches(SPATIUS_SDK_AUDIO_SELECTOR)) {
    return true;
  }

  return Boolean(node.querySelector(SPATIUS_SDK_AUDIO_SELECTOR));
}

export function installSpatiusSdkAudioElementGuard(onCleanup?: SpatiusSdkAudioCleanupObserver) {
  if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
    return () => {};
  }

  const runCleanup = () => {
    const result = silenceSpatiusSdkAudioElementsForLipSync();
    if (result.removedCount > 0 || result.mutedCount > 0) {
      onCleanup?.(result);
    }
  };

  runCleanup();

  const observer = new MutationObserver((mutations) => {
    if (
      mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(nodeContainsSpatiusSdkAudioElement)
      )
    ) {
      runCleanup();
    }
  });

  observer.observe(document.body, {
    childList: true,
  });

  return () => {
    observer.disconnect();
  };
}
